import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, MessageSquare, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService } from '../../services/api';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';

interface Notification {
  type: string;
  title: string;
  message: string;
  ticketId: string;
  createdAt: string;
  _id: string;
  isRead: boolean;
  priority: string;
}

interface NotificationStats {
  openTickets: number;
  inProgressTickets: number;
  waitingResponseTickets: number;
  unrespondedTickets: number;
  totalActive: number;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    openTickets: 0,
    inProgressTickets: 0,
    waitingResponseTickets: 0,
    unrespondedTickets: 0,
    totalActive: 0
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fetchInFlight = useRef(false);
  const lastFetchAt = useRef(0);

  const fetchNotifications = useCallback(async () => {
    // Evitar tempestade de pedidos (429) quando socket dispara vários eventos
    const now = Date.now();
    if (fetchInFlight.current || now - lastFetchAt.current < 5000) return;
    fetchInFlight.current = true;
    lastFetchAt.current = now;
    try {
      setIsLoading(true);
      
      const [statsData, persistentNotifications] = await Promise.all([
        apiService.getTicketStats(),
        apiService.getNotifications({ limit: 10, unreadOnly: true })
      ]);

      // Processar notificações persistentes
      if (persistentNotifications?.data?.notifications) {
        const persistentNotifs = persistentNotifications.data.notifications.map((notif: any) => ({
          type: notif.type,
          title: notif.title,
          message: notif.message,
          ticketId: notif.data?.ticketId,
          createdAt: notif.createdAt,
          isRead: notif.isRead,
          priority: notif.priority
        }));
        setNotifications(persistentNotifs);
      }

      // Processar estatísticas
      if (statsData?.data) {
        setStats(statsData.data);
      }
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setIsLoading(false);
      fetchInFlight.current = false;
    }
  }, []);

  // Callbacks estáveis via ref — evita reconnect do socket a cada render
  const refreshRef = useRef(fetchNotifications);
  refreshRef.current = fetchNotifications;
  const onSocketEvent = useCallback(() => {
    refreshRef.current();
  }, []);

  const { isConnected } = useRealtimeNotifications({
    onNotification: onSocketEvent,
    onNewTicket: onSocketEvent,
    onNewMessage: onSocketEvent,
    onBroadcast: onSocketEvent
  });

  useEffect(() => {
    fetchNotifications();
    
    // Atualizar notificações a cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_ticket':
        return <MessageSquare className="h-4 w-4 text-blue-600" />;
      case 'new_message':
        return <MessageSquare className="h-4 w-4 text-green-600" />;
      case 'ticket_assigned':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case 'ticket_closed':
        return <CheckCircle className="h-4 w-4 text-gray-600" />;
      case 'admin_broadcast':
        return <Bell className="h-4 w-4 text-red-600" />;
      case 'system_announcement':
        return <AlertCircle className="h-4 w-4 text-purple-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'new_ticket':
        return 'bg-blue-50 border-blue-200';
      case 'new_message':
        return 'bg-green-50 border-green-200';
      case 'ticket_assigned':
        return 'bg-orange-50 border-orange-200';
      case 'ticket_closed':
        return 'bg-gray-50 border-gray-200';
      case 'admin_broadcast':
        return 'bg-red-50 border-red-200';
      case 'system_announcement':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const totalNotifications = notifications.length;

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.isRead) {
        await apiService.markNotificationAsRead(notification._id);
        // Atualizar a notificação localmente
        setNotifications(prev => 
          prev.map(notif => 
            notif._id === notification._id 
              ? { ...notif, isRead: true }
              : notif
          )
        );
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  return (
    <div className="relative">
      {/* Botão do sino */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="h-6 w-6" />
        {totalNotifications > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {totalNotifications > 99 ? '99+' : totalNotifications}
          </span>
        )}
        {/* Indicador de conexão em tempo real */}
        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
          isConnected() ? 'bg-green-500' : 'bg-gray-400'
        }`} title={isConnected() ? 'Conectado em tempo real' : 'Desconectado'} />
      </button>

      {/* Dropdown de notificações */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notificações</h3>
                <p className="text-sm text-gray-600">
                  {totalNotifications > 0 
                    ? `${totalNotifications} notificação${totalNotifications > 1 ? 'ões' : ''} não lida${totalNotifications > 1 ? 's' : ''}`
                    : 'Nenhuma notificação nova'
                  }
                </p>
              </div>
              {totalNotifications > 0 && (
                <button
                  onClick={async () => {
                    try {
                      await apiService.markAllNotificationsAsRead();
                      setNotifications(prev => 
                        prev.map(notif => ({ ...notif, isRead: true }))
                      );
                    } catch (error) {
                      console.error('Erro ao marcar todas como lidas:', error);
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Carregando...</p>
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification, index) => (
                  <div
                    key={index}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${getNotificationColor(notification.type)} ${
                      notification.isRead ? 'opacity-75' : 'border-l-4 border-l-blue-500'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Nova
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notification.createdAt).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Nenhuma notificação</p>
              </div>
            )}
          </div>

          {/* Estatísticas */}
          {stats.totalActive > 0 && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-center">
                  <p className="font-medium text-gray-900">{stats.openTickets}</p>
                  <p className="text-gray-600">Abertos</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">{stats.waitingResponseTickets}</p>
                  <p className="text-gray-600">Aguardando</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">{stats.inProgressTickets}</p>
                  <p className="text-gray-600">Em Andamento</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">{stats.unrespondedTickets}</p>
                  <p className="text-gray-600">Sem Resposta</p>
                </div>
              </div>
            </div>
          )}

          {/* Botão de fechar */}
          <div className="p-2 border-t border-gray-200">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Overlay para fechar ao clicar fora */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
